import React, { useState, useEffect } from "react";
import { Play, MoreVertical, Eye, Calendar, Clock, Tag, Film, FileText, RefreshCw, Download } from "lucide-react";
import { AppBadge, ActionMenu, ActionMenuItem, ActionMenuDivider } from "../ui";
import apiClient from "../../utils/apiClient";
import "./Videos.scss";

const formatDuration = (minutes) => {
  if (!minutes) return null;
  const totalSec = Math.round(minutes * 60);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "Asia/Kolkata",
  });
};

const VideoCard = ({ video, role, currentUserId, onPlay, onEdit, onDelete, onLinkMaterial, onSyncFromZoom, onDownload, downloading = false, syncing = false, linkedMaterialCount = 0 }) => {
  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  const isAdmin = role === "admin" || role === "superadmin";
  const isInstructor = role === "instructor";
  const showMenu = isAdmin || isInstructor;
  const canEdit = isAdmin || isInstructor;
  // Instructor can only delete their own manual uploads; admin can delete anything
  const canDelete = isAdmin || (isInstructor && video.uploadType === "manual" && 
    (video.uploadedBy?._id || video.uploadedBy) === currentUserId);
  // Per-video Zoom re-sync — only meaningful for Zoom-sourced recordings
  // that have a meetingId (so the BE can fetch them by ID).
  const canSyncZoom = !!onSyncFromZoom
    && video.provider === "zoom"
    && !!video.meetingId
    && (isAdmin || isInstructor);
  const isReady = video.status === "ready";
  const isProcessing = video.status === "processing";
  const isFailed = video.status === "failed";
  // Download is admin/superadmin only — never offered to students or instructors.
  const canDownload = !!onDownload && isAdmin && isReady;

  useEffect(() => {
    if (video.thumbnailS3Key) {
      apiClient
        .get(`/videos/${video._id}/thumbnail`)
        .then(({ data }) => {
          if (data.success) setThumbnailUrl(data.data.url);
        })
        .catch(() => {});
    }
  }, [video._id, video.thumbnailS3Key]);

  const handleClick = () => {
    if (isReady && onPlay) onPlay(video);
  };

  return (
    <div className="video-card" onClick={handleClick}>
      {/* Thumbnail */}
      <div className="video-card__thumbnail">
        {thumbnailUrl ? (
          <img src={thumbnailUrl} alt={video.title} loading="lazy" />
        ) : (
          <div className="video-card__thumbnail-placeholder">
            <Film />
          </div>
        )}

        {isReady && (
          <div className="video-card__play-overlay">
            <div className="video-card__play-overlay-btn">
              <Play />
            </div>
          </div>
        )}

        {video.duration > 0 && (
          <span className="video-card__duration">{formatDuration(video.duration)}</span>
        )}

        {isProcessing && (
          <div className="video-card__status-badge">
            <AppBadge variant="outline" color="amber">Processing</AppBadge>
          </div>
        )}
        {isFailed && (
          <div className="video-card__status-badge">
            <AppBadge variant="outline" color="rose">Failed</AppBadge>
          </div>
        )}
      </div>

      {/* Body */}
      <div className="video-card__body">
        <div className="video-card__title-row">
          <h4 className="video-card__title">{video.title}</h4>

          {showMenu && (canEdit || canDelete || canSyncZoom || canDownload) && (
            <ActionMenu trigger={<MoreVertical size={16} />}>
              {canEdit && (
                <ActionMenuItem onClick={() => onEdit && onEdit(video)}>
                  Edit
                </ActionMenuItem>
              )}
              {canDownload && (
                <ActionMenuItem onClick={() => !downloading && onDownload(video)}>
                  <Download size={13} style={{ marginRight: 6 }} />
                  {downloading ? "Preparing…" : "Download"}
                </ActionMenuItem>
              )}
              {canEdit && onLinkMaterial && (
                <ActionMenuItem onClick={() => onLinkMaterial(video)}>
                  <FileText size={13} style={{ marginRight: 6 }} />
                  Link Material
                </ActionMenuItem>
              )}
              {canSyncZoom && (
                <>
                  {(canEdit || onLinkMaterial) && <ActionMenuDivider />}
                  <ActionMenuItem
                    onClick={() => !syncing && onSyncFromZoom(video)}
                  >
                    <RefreshCw size={13} style={{ marginRight: 6 }} className={syncing ? "spin" : undefined} />
                    {syncing ? "Syncing…" : "Sync from Zoom"}
                  </ActionMenuItem>
                </>
              )}
              {canDelete && (
                <>
                  <ActionMenuDivider />
                  <ActionMenuItem danger onClick={() => onDelete && onDelete(video)}>
                    Delete
                  </ActionMenuItem>
                </>
              )}
            </ActionMenu>
          )}
        </div>

        {video.topic && (
          <div className="video-card__topic">
            <Tag size={12} />
            {video.topic}
          </div>
        )}

        <div className="video-card__meta">
          <span>
            <Calendar size={12} />
            {formatDate(video.recordedAt)}
          </span>
          <span>
            <Eye size={12} />
            {video.viewCount || 0} views
          </span>
          {linkedMaterialCount > 0 && (
            <span>
              <FileText size={12} />
              {linkedMaterialCount} material{linkedMaterialCount > 1 ? "s" : ""}
            </span>
          )}
          {video.uploadType === "auto" && (
            <span>
              <Clock size={12} />
              Auto
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoCard;
